<template>
  <div
    ref="domNode"
    class="relative flex items-center w-full p-0 border appearance-none cursor-pointer transition rounded-md shadow-sm select-box duration-400 hover_border-gray-400"
    :class="[{ active: isOpen }, `size-${size}`, `select-box--${theme}`]"
  >
    <button
      ref="buttonRef"
      :disabled="disabled"
      class="flex items-center justify-between w-full h-full text-left bg-transparent collapsed"
      data-testid="selector-selected-item"
      @click.prevent="toggleActive"
    >
      <slot v-if="multi && (modelValue === null || !(modelValue as Option[]).length)" class="flex-grow text-gray-400">
        {{ placeholder }}
      </slot>
      <slot v-else-if="multi && Array.isArray(modelValue)" name="current-multi" :items="modelValue as Option[]" />
      <slot v-else-if="!multi && modelValue" name="current-single" :item="modelValue as Option" />
      <div v-else class="flex-grow text-gray-400">{{ placeholder }}</div>
      <slot name="icon">
        <ChevronDownIcon class="flex-shrink-0 text-gray-600 transition duration-200 icon" />
      </slot>
    </button>

    <transition name="fade">
      <div
        v-show="isOpen"
        ref="dropdownRef"
        class="absolute z-10 order-1 bg-white border border-gray-600 text-current rounded-md shadow-sm options-container"
        :class="[containerClasses || 'w-full mt-1 right-0']"
        :style="{ top: dropdownVerticalPosition }"
      >
        <slot name="search" />

        <div class="overflow-y-auto option-scrollable" data-testid="dropdown-container">
          <template v-if="isDefaultSlot">
            <slot />
          </template>

          <template v-else>
            <button v-for="option in options" :key="option.id" class="option" @click.prevent="onSelected(option)">
              <slot
                v-if="multi"
                name="option"
                :item="option"
                :selected="Array.isArray(modelValue) && modelValue.some(({ id }) => id === option.id)"
              />
              <slot v-else name="option" :item="option" :selected="!!(modelValue && option.id === (modelValue as Option).id)" />
            </button>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
export interface Option {
  id: string | number
  name: string
}
</script>

<script lang="ts" setup>
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import isChildOf from '@/lib/is-child-of'

const props = withDefaults(
  defineProps<{
    options: Option[]
    modelValue: Option | Option[] | null
    placeholder?: string
    disabled?: boolean
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
    theme?: 'light' | 'dark'
    containerClasses?: string
    isDropdownOpen?: boolean
    multi?: boolean
  }>(),
  {
    placeholder: 'No selection',
    disabled: false,
    size: 'md',
    theme: 'light',
    containerClasses: '',
    isDropdownOpen: false,
    multi: false,
  },
)

const emit = defineEmits(['toggle', 'update:modelValue'])
const slots = defineSlots<{
  default?: () => VNode
  icon: () => VNode
  search: () => VNode
  'current-single': (param: { item: Option }) => VNode
  'current-multi': (param: { items: Option[] }) => VNode
  option: (params: { item: Option; selected: boolean }) => VNode
}>()

const isOpen = ref(false)
const domNode = ref<HTMLElement | undefined>()
const isDefaultSlot = computed(() => slots.default)

const { buttonRef, dropdownRef, calculateDropdownVerticalPosition, dropdownVerticalPosition } = useDropdownDirection()

watch(
  () => props.isDropdownOpen,
  val => (isOpen.value = val),
)

onMounted(() => document.addEventListener('click', _removeActive))
onUnmounted(() => document.removeEventListener('click', _removeActive))

function onSelected(option: Option) {
  if (props.multi) {
    const selected = Array.isArray(props.modelValue) ? props.modelValue : []
    const index = selected.findIndex(({ id }) => id === option.id)

    if (index === -1) {
      selected.push(option)
    } else {
      selected.splice(index, 1)
    }

    emit('update:modelValue', selected)
  } else {
    isOpen.value = false
    emit('update:modelValue', option)
  }
}

function toggleActive() {
  isOpen.value = !isOpen.value && !props.disabled
  emit('toggle', isOpen.value)

  nextTick(() => {
    calculateDropdownVerticalPosition()
  })
}

function _removeActive(e: MouseEvent) {
  if (isChildOf(e.target as Node, domNode.value)) return
  isOpen.value = false
  emit('toggle', false)
}
</script>

<style lang="stylus" scoped>
.fade-enter-active, .fade-leave-active
  transition opacity 0.4s, max-height 0.4s

.fade-enter, .fade-leave-to
  opacity: 0
  max-height: 0

.select-box
  &--light
    @apply bg-white text-gray-750

  &--dark
    @apply bg-gray-900 text-white border-gray-600
    .options-container
      @apply bg-gray-900 text-white border-0
    .icon
      @apply text-white

  .options-container
    max-height 0
    pointer-events none
    overflow hidden

    button.option
      @apply block bg-transparent text-left p-0 w-full hover_text-black hover_bg-gray-450

    .option-scrollable
      transition max-height 0s 0.4s ease-out
      max-height 0
      overflow hidden

      &::-webkit-scrollbar
        width 8px
        background rgba(0,0,0,0.12)
        border-radius 4px

      &::-webkit-scrollbar-thumb
        background rgb(117,117,117)
        border-radius 4px

  &.active
    @apply ring-2 ring-gray-900

    .icon
      transform rotate(180deg)

    .options-container
      transition opacity 0.3s, max-height 0.25s
      opacity 1
      max-height 500px
      pointer-events auto

      .option-scrollable
        transition max-height 0.25s
        max-height 200px
        overflow-y auto

  &.size-xs button.collapsed
    @apply pl-2 pr-2 py-1 text-xs
    .icon
      @apply w-4 h-4
  &.size-sm button.collapsed
    @apply pl-3 pr-3 py-2 text-sm
    .icon
      @apply w-4 h-4
    line-height 1.15rem
  &.size-md button.collapsed
    @apply pl-4 pr-3 py-3 text-sm
    .icon
      @apply w-5 h-5
  &.size-lg button.collapsed
    @apply pl-5 pr-4 py-4
    .icon
      @apply w-6 h-6
  &.size-xl button.collapsed
    @apply px-6 py-5 text-lg
    .icon
      @apply w-8 h-8
</style>
